import { useQuery } from "@tanstack/react-query";
import { LabDto } from "../../../../../customer-experience/src/lib/ShiOneClient";
import { CACHE_KEY_LABS, CACHE_KEY_LABS_PICKLIST } from "../constants";
import { useContext } from "react";
import { LabsApiContext } from "../LabsApiContext";
import type { ServerFilterCategory } from "../types";

export function useGetAllLabs() {
  const labsApi = useContext(LabsApiContext);
  if (!labsApi) throw new Error("LabsApiContext not provided");
  const useDefinitionsPayload = labsApi.useDefinitionsPayload ?? false;
  const compareLegacyAndDefinitionsPayloads =
    labsApi.compareLegacyAndDefinitionsPayloads ?? false;

  const fetchAllLabs = async (): Promise<LabDto[]> => {
    if (!compareLegacyAndDefinitionsPayloads) {
      return useDefinitionsPayload
        ? ((await labsApi.getLabsDefinition()) ?? [])
        : ((await labsApi.getAllLabs()) ?? []);
    }

    const [legacyResult, definitionResult] = await Promise.allSettled([
      labsApi.getAllLabs(),
      labsApi.getLabsDefinition(),
    ]);

    if (
      definitionResult.status === "fulfilled" &&
      legacyResult.status === "fulfilled"
    ) {
      compareLabDefinitions(legacyResult.value, definitionResult.value);
    }

    const primaryResult = useDefinitionsPayload
      ? definitionResult
      : legacyResult;
    const secondaryResult = useDefinitionsPayload
      ? legacyResult
      : definitionResult;

    if (secondaryResult.status === "rejected") {
      console.warn(
        useDefinitionsPayload
          ? "Labs legacy parity request failed"
          : "Labs definition parity request failed",
        {
          error: secondaryResult.reason,
        },
      );
    }

    if (primaryResult.status === "rejected") {
      throw primaryResult.reason;
    }

    return primaryResult.value ?? [];
  };

  return useQuery<LabDto[], Error>({
    queryKey: [
      CACHE_KEY_LABS,
      useDefinitionsPayload ? "definitions" : "legacy",
    ],
    queryFn: fetchAllLabs,
  });
}

function compareLabDefinitions(
  legacyLabs: LabDto[] | undefined,
  definitionLabs: LabDto[] | undefined,
) {
  const legacyByKey = new Map(
    (legacyLabs ?? []).map((lab) => [getLabComparisonKey(lab), lab]),
  );
  const definitionByKey = new Map(
    (definitionLabs ?? []).map((lab) => [getLabComparisonKey(lab), lab]),
  );
  const mismatchedKeys = [
    ...new Set([...legacyByKey.keys(), ...definitionByKey.keys()]),
  ].filter((key) => {
    const legacyLab = legacyByKey.get(key);
    const definitionLab = definitionByKey.get(key);
    return (
      !legacyLab ||
      !definitionLab ||
      legacyLab.productName !== definitionLab.productName ||
      legacyLab.uniqueId !== definitionLab.uniqueId ||
      legacyLab.isOnDemandLab !== definitionLab.isOnDemandLab
    );
  });

  if (mismatchedKeys.length > 0 || legacyByKey.size !== definitionByKey.size) {
    console.warn("Labs definition parity mismatch", {
      legacyCount: legacyByKey.size,
      definitionCount: definitionByKey.size,
      mismatchedKeys,
    });
  }
}

function getLabComparisonKey(lab: LabDto) {
  return lab.uniqueId?.trim() || `id:${lab.id}`;
}

export function useGetLabFilterOptions() {
  const labsApi = useContext(LabsApiContext);
  if (!labsApi) throw new Error("LabsApiContext not provided");
  const useDefinitionsPayload = labsApi.useDefinitionsPayload ?? false;

  const fetchLabFilterOptions = async (): Promise<ServerFilterCategory[]> => {
    if (!labsApi.getLabFilterOptions) {
      return [];
    }

    return (await labsApi.getLabFilterOptions()) ?? [];
  };

  return useQuery<ServerFilterCategory[], Error>({
    queryKey: [
      CACHE_KEY_LABS,
      CACHE_KEY_LABS_PICKLIST,
      useDefinitionsPayload ? "definitions" : "legacy",
    ],
    queryFn: fetchLabFilterOptions,
  });
}
